import React from "react";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import TextFilter from "@cloudscape-design/components/text-filter";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { walletGroupsStore } from "../stores/walletGroups";
import { walletGroupsEditModalStore } from "../stores/walletGroupsEditModal";
import { userStore } from "../stores/access";
import { configStore } from "../stores/config";

export const getWalletGroupEditState = (item, availableCurrencies) => {
	const config = item.verificationConfig || {};
	return {
		show: true,
		section: "verification",
		loaded: true,
		id: item.id,
		method: item.method || "",
		enabled: config.valid ? Boolean(config.enabled) : false,
		types: config.valid && Array.isArray(config.types) ? config.types : [],
		currencyMode: config.valid && config.currencyMode === "selected" ? "selected" : "all",
		currencies: config.valid && Array.isArray(config.currencies) ? config.currencies : [],
		partnerExceptions: config.valid && Array.isArray(config.partnerExceptions) ? config.partnerExceptions : [],
		partnerExceptionsText: config.valid && Array.isArray(config.partnerExceptions) ? config.partnerExceptions.join("\n") : "",
		availableCurrencies,
		sourceValid: config.valid !== false,
		sourceError: config.error || "",
		rawVerification: item.verification,
		changed: false,
	};
};

const WalletGroupImage = ({ item }) => {
	const [failed, setFailed] = React.useState(false);
	if (!item.img || failed) return <Box color="text-status-inactive">No image</Box>;
	return <img src={item.img} alt={`${item.method} wallet group`} onError={() => setFailed(true)} style={{ display: "block", width: 48, height: 32, objectFit: "contain" }} />;
};

export class WalletGroups extends React.Component {
	state = {
		searchText: this.props.walletGroups.currentSearch,
	};

	componentDidMount() {
		this.props.Interface.loadWalletGroups();
	}

	applySearch = () => {
		const search = this.state.searchText.trim();
		this.props.setWalletGroups({
			...this.props.walletGroups,
			currentSearch: search,
			pagination: { ...this.props.walletGroups.pagination, current: 1 },
		});
		setTimeout(() => this.props.Interface.loadWalletGroups(), 0);
	};

	handleSearchKeyDown = (event) => {
		if (event.key !== "Enter" || event.nativeEvent?.isComposing) return;
		event.preventDefault();
		this.applySearch();
	};

	openEditModal = (item) => {
		this.props.setEditModal(getWalletGroupEditState(item, this.props.walletGroups.currencies));
	};

	openValidationModal = (item) => {
		this.props.setEditModal({ ...getWalletGroupEditState(item, this.props.walletGroups.currencies), section: "validation", project: this.props.user.brand?.id });
	};

	formatVerification = (item) => {
		const config = item.verificationConfig;
		if (!config || !config.valid) {
			return <StatusIndicator type="error">Invalid configuration</StatusIndicator>;
		}
		if (!config.enabled) return <StatusIndicator type="stopped">Disabled</StatusIndicator>;
		const scope = config.currencyMode === "selected" ? config.currencies.join(", ") : "all currencies";
		const partnerScope = config.partnerExceptions?.length ? ` · partner exceptions: ${config.partnerExceptions.join(", ")}` : "";
		return <StatusIndicator type="success">{`${config.types.join(" + ")} · ${scope}${partnerScope}`}</StatusIndicator>;
	};

	render() {
		const { walletGroups } = this.props;
		return (
			<>
				{walletGroups.loaded && walletGroups.error.text ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading wallet groups">
							{walletGroups.error.text}
						</Alert>
					</Box>
				) : null}
				<Table
					stickyHeader
					trackBy="id"
					loading={!walletGroups.loaded}
					items={walletGroups.data}
					filter={
						<div onKeyDown={this.handleSearchKeyDown}>
							<SpaceBetween direction="horizontal" size="xs">
								<TextFilter
									filteringPlaceholder="Search by Method"
									filteringText={this.state.searchText}
									onChange={({ detail }) => this.setState({ searchText: detail.filteringText })}
								/>
								<Button iconName="search" onClick={this.applySearch}>Search</Button>
							</SpaceBetween>
						</div>
					}
					sortingColumn={{ sortingField: walletGroups.sorting.sortingColumn.column }}
					sortingDescending={walletGroups.sorting.isDescending}
					onSortingChange={({ detail }) => {
						this.props.setWalletGroups({
							...walletGroups,
							sorting: {
								sortingColumn: {
									column: detail.sortingColumn.sortingField,
									order: detail.isDescending ? "DESC" : "ASC",
								},
								isDescending: detail.isDescending,
							},
						});
						setTimeout(() => this.props.Interface.loadWalletGroups(), 0);
					}}
					columnDefinitions={[
						{
							id: "id",
							header: "ID",
							sortingField: "id",
							cell: (item) => <Button iconName="edit" onClick={() => this.openEditModal(item)}>{item.id}</Button>,
						},
						{ id: "method", header: "Method", sortingField: "method", cell: (item) => item.method || "-" },
						{ id: "img", header: "Image", cell: (item) => <WalletGroupImage item={item} /> },
						{ id: "verification", header: "Verification", sortingField: "verification", cell: (item) => this.formatVerification(item) },
						{ id: "validation", header: "Withdrawal validation", cell: (item) => <Button onClick={() => this.openValidationModal(item)}>Configure</Button> },
					]}
					pagination={
						<Pagination
							disabled={!walletGroups.loaded}
							currentPageIndex={walletGroups.pagination.current}
							pagesCount={walletGroups.pagination.total}
							onChange={({ detail }) => {
								this.props.setWalletGroups({ ...walletGroups, pagination: { ...walletGroups.pagination, current: detail.currentPageIndex } });
								setTimeout(() => this.props.Interface.loadWalletGroups(), 0);
							}}
						/>
					}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{ pageSize: this.props.config.walletGroups.perPage }}
							pageSizePreference={{ title: "Wallet groups per page", options: this.props.config.walletGroups.preferences.perPage }}
							onConfirm={({ detail }) => {
								localStorage.setItem("walletGroups.perPage", detail.pageSize);
								this.props.setConfig({ ...this.props.config, walletGroups: { ...this.props.config.walletGroups, perPage: detail.pageSize } });
								setTimeout(() => this.props.Interface.loadWalletGroups(), 0);
							}}
						/>
					}
					header={<Header description="Verification and withdrawal validation settings for wallet payment groups">Wallet groups ({walletGroups.pagination.count})</Header>}
					empty="No wallet groups found"
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const walletGroups = useRecoilValue(walletGroupsStore);
		const setWalletGroups = useSetRecoilState(walletGroupsStore);
		const setEditModal = useSetRecoilState(walletGroupsEditModalStore);
		const user = useRecoilValue(userStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		return <Component {...props} user={user} walletGroups={walletGroups} setWalletGroups={setWalletGroups} setEditModal={setEditModal} config={config} setConfig={setConfig} />;
	};
}

export default withRecoil(WalletGroups);
